import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  CardHeader,
  IconButton,
  Link,
  Rating,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
} from '@firebase/firestore';
import { deleteObject, ref } from 'firebase/storage';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import FolderZipOutlinedIcon from '@mui/icons-material/FolderZipOutlined';
import CircleIcon from '@mui/icons-material/Circle';
import PendingIcon from '@mui/icons-material/Pending';
import DoneIcon from '@mui/icons-material/Done';
import MarkUnreadChatAltOutlinedIcon from '@mui/icons-material/MarkUnreadChatAltOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useTheme } from '@mui/material/styles';

import { db, storage } from '../service/Firebase-config';
import { useAuth } from '../service/AuthContext';
import Title from './Title';
import DialogEditHomeworkByStudent from '../form/DialogEditHomeworkByStudent';
import DialogEditRating from '../form/DialogEditRating';
import TablePaginationActions from '../common/TablePagination';
import { useGlobalContext } from '../context/GlobalContext';
import { useHomeworkContext } from '../context/HomeworkContext';
import { getFileExtension, exportToExcel } from '../common/util';

export default function HomeworkTable() {
  const { userInfo } = useAuth();
  const { selectedUser } = useGlobalContext();
  const { openEditHw, setOpenEditHw, setSelectedHw } = useHomeworkContext();

  const theme = useTheme();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [homeworks, setHomeworks] = useState([]);
  const [data, setData] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [subjectFilter, setSubjectFilter] = useState('');
  const [teacherFilter, setTeacherFilter] = useState('');
  const [queryText, setQueryText] = useState('');
  const [teacherList, setTeacherList] = useState([]);
  const [fileStudent, setFileStudent] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [openEditRating, setOpenEditRating] = useState(null);
  const [selectedDeleteHw, setSelectedDeleteHw] = useState(null);

  const openEditHomework = async (hw) => {
    if (userInfo?.role === 'student') {
      const homeworkRef = doc(
        db,
        `students/${selectedUser.uid}/homeworks/`,
        hw.id
      );

      await updateDoc(homeworkRef, {
        isHwOpen: true,
        isNewComment: false,
      });

      const studentRef = doc(db, 'students', selectedUser.uid);
      const docSnapshot = await getDoc(studentRef);

      if (docSnapshot.exists()) {
        const newUpdates = (docSnapshot.data().newUpdate || []).filter(
          (update) => update.homeworkId !== hw.id
        );

        await updateDoc(studentRef, { newUpdate: newUpdates });
      }
    }

    setSelectedHw(hw);
    setFileStudent(hw.fileStudent || []);
    setSelectedTeacher(
      teacherList.find((teacher) => teacher.id === hw.teacherUid) || null
    );
    setOpenEditHw(true);
  };

  const closeEditHomework = () => {
    setOpenEditHw(false);
  };

  const handleDeleteHomework = async () => {
    if (!selectedDeleteHw || !selectedUser?.uid) return;

    try {
      const studentRef = doc(db, 'students', selectedUser.uid);
      const teacherRef = doc(db, 'teachers', selectedDeleteHw.teacherUid);
      const homeworkRef = doc(
        db,
        `students/${selectedUser.uid}/homeworks/`,
        selectedDeleteHw.id
      );

      const studentFiles = selectedDeleteHw.fileStudent || [];
      const teacherFiles = selectedDeleteHw.fileTeacher || [];
      const allFiles = [...studentFiles, ...teacherFiles];

      // 1. storage 삭제
      await Promise.all(
        allFiles.map(async (file) => {
          if (!file?.savedName) return;

          const fileRef = ref(
            storage,
            `file/${selectedUser.uid}/${selectedDeleteHw.id}/${file.savedName}`
          );

          try {
            await deleteObject(fileRef);
          } catch (error) {
            console.error('Failed to delete storage file:', file.savedName, error);
          }
        })
      );

      // 2. student newUpdate 정리
      const studentSnap = await getDoc(studentRef);
      if (studentSnap.exists()) {
        const newUpdate = studentSnap.data().newUpdate || [];

        const filtered = newUpdate.filter(
          (item) => item?.homeworkId !== selectedDeleteHw.id
        );

        if (filtered.length !== newUpdate.length) {
          await updateDoc(studentRef, { newUpdate: filtered });
        }
      }

      // 3. teacher newUpdate 정리
      if (selectedDeleteHw.teacherUid) {
        const teacherSnap = await getDoc(teacherRef);

        if (teacherSnap.exists()) {
          const newUpdate = teacherSnap.data().newUpdate || [];

          const filtered = newUpdate.filter(
            (item) => item?.homeworkId !== selectedDeleteHw.id
          );

          if (filtered.length !== newUpdate.length) {
            await updateDoc(teacherRef, { newUpdate: filtered });
          }
        }
      }

      // 4. homework 삭제
      await deleteDoc(homeworkRef);

      setSelectedDeleteHw(null);
    } catch (error) {
      console.error('Failed to delete homework:', error);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleQuery = (event) => {
    const newQueryText = event.target.value.toLowerCase();
    setQueryText(newQueryText);
    filterData(
      newQueryText,
      teacherFilter,
      subjectFilter,
      startDate,
      endDate
    );
  };

  const handleSubjectFilterChange = (event) => {
    const subject = event.target.value;
    setSubjectFilter(subject);
    filterData(queryText, teacherFilter, subject, startDate, endDate);
  };

  const handleTeacherFilterChange = (event) => {
    const teacher = event.target.value;
    setTeacherFilter(teacher);
    filterData(queryText, teacher, subjectFilter, startDate, endDate);
  };

  const filterData = (queryText, teacher, subject, start, end) => {
    let filteredData = homeworks;

    if (teacher) {
      filteredData = filteredData.filter((row) => row.teacherUid === teacher);
    }

    if (subject) {
      filteredData = filteredData.filter((row) => row.subject === subject);
    }

    if (queryText) {
      filteredData = filteredData.filter((row) =>
        (row.homework || '').toLowerCase().includes(queryText.toLowerCase())
      );
    }

    if (start) {
      filteredData = filteredData.filter(
        (row) => new Date(row.createdDate.seconds * 1000) >= start.toDate()
      );
    }

    if (end) {
      filteredData = filteredData.filter(
        (row) => new Date(row.createdDate.seconds * 1000) <= end.toDate()
      );
    }

    setData(filteredData);
  };

  useEffect(() => {
    filterData(queryText, teacherFilter, subjectFilter, startDate, endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryText, teacherFilter, subjectFilter, startDate, endDate, homeworks]);

  useEffect(() => {
    if (!selectedUser?.uid) return;

    setSubjectFilter('');
    setTeacherFilter('');
    setQueryText('');
    setStartDate(null);
    setEndDate(null);
    setPage(0);

    const collectionRef = collection(db, `students/${selectedUser.uid}/homeworks`);
    const orderedQuery = query(collectionRef, orderBy('createdDate', 'desc'));

    const unsubscribeHomeworks = onSnapshot(orderedQuery, async (snapshot) => {
      const newHomeworks = snapshot.docs.map((docItem) => ({
        ...docItem.data(),
        id: docItem.id,
      }));

      setHomeworks(newHomeworks);
      setData(newHomeworks);

      const teacherIds = [...new Set(newHomeworks.map((hw) => hw.teacherUid).filter(Boolean))];

      const teacherDocs = await Promise.all(
        teacherIds.map(async (id) => {
          const teacherDoc = await getDoc(doc(db, 'teachers', id));
          return {
            id,
            ...(teacherDoc.exists() ? teacherDoc.data() : {}),
          };
        })
      );

      setTeacherList(teacherDocs);
    });

    return () => unsubscribeHomeworks();
  }, [selectedUser]);

  const uniqueSubjects = [...new Set(homeworks.map((hw) => hw.subject).filter(Boolean))];

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Title>Homework List</Title>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {userInfo?.role === 'admin' && (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                sx={{
                  width: '165px',
                  marginRight: 2,
                }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                sx={{
                  width: '165px',
                }}
              />
            </LocalizationProvider>
          )}

          <TextField
            label="Search Homework"
            size="small"
            variant="outlined"
            onChange={handleQuery}
            value={queryText}
            sx={{ marginLeft: 2 }}
          />

          <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
            <InputLabel id="teacher-filter-label">Teacher</InputLabel>
            <Select
              labelId="teacher-filter-label"
              id="teacher-filter"
              value={teacherFilter}
              label="Teacher"
              onChange={handleTeacherFilterChange}
            >
              <MenuItem value="">
                <em>All Teachers</em>
              </MenuItem>
              {teacherList.map((teacher) => (
                <MenuItem key={teacher.id} value={teacher.id}>
                  {teacher.displayName}
                  <Box ml={1} component="span">
                    {teacher?.active ? (
                      <CircleIcon sx={{ color: '#5Dbb63', fontSize: 12 }} />
                    ) : (
                      <CircleIcon sx={{ color: '#FF0000', fontSize: 12 }} />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
            <InputLabel id="subject-filter-label">Subject</InputLabel>
            <Select
              labelId="subject-filter-label"
              id="subject-filter"
              value={subjectFilter}
              label="Subject"
              onChange={handleSubjectFilterChange}
            >
              <MenuItem value="">
                <em>All Subjects</em>
              </MenuItem>
              {uniqueSubjects.map((subject) => (
                <MenuItem key={subject} value={subject}>
                  {subject}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {userInfo?.role === 'admin' && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              sx={{ marginLeft: 2 }}
              onClick={() =>
                exportToExcel(data, teacherList, selectedUser?.displayName || '')
              }
            >
              Export
            </Button>
          )}
        </Box>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Homework</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Subject</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Teacher</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Rating</TableCell>
            {userInfo?.role === 'admin' && (
              <TableCell sx={{ fontWeight: 'bold' }}>Journal</TableCell>
            )}
            <TableCell sx={{ fontWeight: 'bold' }}>File</TableCell>
            {userInfo?.role === 'admin' && (
              <TableCell align="center" sx={{ width: 56 }} />
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {(rowsPerPage > 0
            ? data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            : data
          ).map((row) => (
            <TableRow
              key={row.id}
              sx={{
                backgroundColor:
                  !row.isHwOpen && userInfo?.role === 'student' ? '#FEF9E7' : 'inherit',
                '&:hover': {
                  backgroundColor:
                    !row.isHwOpen && userInfo?.role === 'student'
                      ? '#FCF3CF'
                      : theme.palette.grey[200],
                  cursor: 'pointer',
                },
              }}
            >
              <TableCell onClick={() => openEditHomework(row)}>
                {new Date(row.createdDate.seconds * 1000).toLocaleString('en-US', {
                  month: 'short',
                  day: '2-digit',
                  year: 'numeric',
                })}
              </TableCell>

              <Tooltip title={row.homework || ''}>
                <TableCell onClick={() => openEditHomework(row)}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span>
                      {row.homework?.length < 40
                        ? row.homework
                        : `${row.homework.substring(0, 40)}...`}
                    </span>

                    {userInfo?.role === 'student' && row.isNewComment && (
                      <MarkUnreadChatAltOutlinedIcon
                        sx={{
                          ml: 2,
                          color: !row.isHwOpen ? '#767676' : theme.palette.primary.main,
                        }}
                        fontSize="small"
                      />
                    )}
                  </Box>
                </TableCell>
              </Tooltip>
              <TableCell onClick={() => openEditHomework(row)}>
                {row.subject}
              </TableCell>

              <TableCell onClick={() => openEditHomework(row)}>
                <CardHeader
                  avatar={
                    <Avatar
                      src={teacherList.find((teacher) => teacher.id === row.teacherUid)?.photoUrl}
                    />
                  }
                  title={
                    <Box display="flex" alignItems="center">
                      <Typography>
                        {teacherList.find((teacher) => teacher.id === row.teacherUid)?.displayName}
                      </Typography>
                      <Box ml={1}>
                        {teacherList.find((teacher) => teacher.id === row.teacherUid)?.active ? (
                          <CircleIcon sx={{ color: '#5Dbb63', fontSize: 12 }} />
                        ) : (
                          <CircleIcon sx={{ color: '#FF0000', fontSize: 12 }} />
                        )}
                      </Box>
                    </Box>
                  }
                />
              </TableCell>

              <TableCell
                onClick={() => userInfo?.role === 'admin' && setOpenEditRating(row)}
              >
                <Rating
                  name="simple-controlled"
                  value={row?.rating || 0}
                  size="small"
                  readOnly
                />
              </TableCell>

              {userInfo?.role === 'admin' && (
                <TableCell onClick={() => openEditHomework(row)}>
                  <Tooltip title={row.journal || 'No journal available'} placement="top-end">
                    <IconButton>
                      {row.journal ? <DoneIcon /> : <PendingIcon />}
                    </IconButton>
                  </Tooltip>
                </TableCell>
              )}

              <TableCell>
                {row.fileStudent &&
                  row.fileStudent.map((file) => (
                    <Tooltip key={file.savedName || file.fileName} title={file.fileName}>
                      <Link variant="caption" href={file.fileUrl} target="_blank">
                        <Box component="span" mx={0.5}>
                          {getFileExtension(file.fileName) === 'pdf' ? (
                            <PictureAsPdfOutlinedIcon color="warning" />
                          ) : (
                            <FolderZipOutlinedIcon color="info" />
                          )}
                        </Box>
                      </Link>
                    </Tooltip>
                  ))}
              </TableCell>

              {userInfo?.role === 'admin' && (
                <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                  <IconButton onClick={() => setSelectedDeleteHw(row)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              )}
            </TableRow>
          ))}

          {emptyRows > 0 && (
            <TableRow style={{ height: 53 * emptyRows }}>
              <TableCell colSpan={userInfo?.role === 'admin' ? 8 : 6} />
            </TableRow>
          )}
        </TableBody>

        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
              colSpan={userInfo?.role === 'admin' ? 8 : 6}
              count={data.length}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{
                inputProps: {
                  'aria-label': 'rows per page',
                },
                native: true,
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      </Table>

      <Dialog
        open={Boolean(selectedDeleteHw)}
        onClose={() => setSelectedDeleteHw(null)}
      >
        <DialogTitle>Delete Homework</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this homework and all related files?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedDeleteHw(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteHomework}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <DialogEditRating
        open={!!openEditRating}
        handleClose={() => setOpenEditRating(null)}
        row={openEditRating}
      />

      <DialogEditHomeworkByStudent
        open={openEditHw}
        closeEditHomework={closeEditHomework}
        fileStudent={fileStudent}
        setFileStudent={setFileStudent}
        selectedTeacher={selectedTeacher}
      />
    </>
  );
}